import { useTranslation } from 'react-i18next';
import { makeStyles, alpha } from '@material-ui/core/styles';
import { Box, Typography } from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

import { SATELLITE_GRID_MAX_WIDTH } from './SatelliteChooser';

/**
 * @constant NOTICE_POINTS
 * @description The guidance shown under the satellite cards, in reading order, as keys under
 * `forms.acquisition.1.notice`. The panel used to carry a single line inviting the user to look the
 * satellites up somewhere else; the choice is made here, so the reasoning to make it belongs here
 * too, with nothing to open and no page to leave.
 * @memberof module:Shared
 */
const NOTICE_POINTS = ['sentinel', 'landsat', 'tip'];

/**
 * @function useStyles
 * @description Hook for styling using Material-UI's makeStyles. Defines the guidance panel that
 * sits under the satellite cards, at the same width as the row above it.
 * @param {Object} theme - Material-UI theme containing color, spacing, and other configurations
 * @memberof module:Shared
 * @returns {Object} Object containing compiled CSS classes for component usage
 */
const useStyles = makeStyles((theme) => ({
  notice: {
    display: 'flex',
    gap: theme.spacing(1.5),
    alignItems: 'flex-start',
    width: '100%',
    maxWidth: SATELLITE_GRID_MAX_WIDTH,
    margin: theme.spacing(0, 'auto', 3),
    padding: theme.spacing(2),
    borderRadius: 10,
    backgroundColor: alpha(theme.palette.primary.main, 0.07),
  },
  noticeIcon: {
    color: theme.palette.primary.main,
  },
  noticeTitle: {
    fontWeight: 600,
  },
  noticeText: {
    color: theme.palette.text.secondary,
    '& + &': {
      marginTop: theme.spacing(1),
    },
  },
}));

/**
 * @function SatelliteNotice
 * @description Explains what the choice between the satellites actually changes - resolution,
 * revisit, how far back the series goes - under the cards it applies to.
 * A component of its own rather than markup inside one chooser: MEPBay has a chooser of its own and
 * so was the one module offering the same two cards with none of the reasoning behind them, and the
 * module that offers both satellites at once had nothing explaining what that third card does.
 * @param {Object} props
 * @param {Boolean} [props.includeCombined] - Whether this module also offers the two satellites
 *  combined, in which case that card gets a line of its own.
 * **Called by:
 * @see {SatelliteChooser} - the shared chooser, and MEPBay's.
 * @memberof module:Shared
 * @returns {JSX.Element} The guidance panel.
 */
const SatelliteNotice = ({ includeCombined = false }) => {
  const classes = useStyles();
  const [t] = useTranslation();
  const points = includeCombined ? [...NOTICE_POINTS.slice(0, -1), 'combined', 'tip'] : NOTICE_POINTS;

  return (
    <Box className={classes.notice}>
      <InfoOutlinedIcon className={classes.noticeIcon} />
      <div>
        <Typography variant="body2" className={classes.noticeTitle}>
          {t('forms.acquisition.1.notice.title')}
        </Typography>
        {points.map((point) => (
          <Typography key={point} variant="body2" className={classes.noticeText}>
            {t(`forms.acquisition.1.notice.${point}`)}
          </Typography>
        ))}
      </div>
    </Box>
  );
};

export default SatelliteNotice;
